import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { rightColumnFieldStyle, rowStyle } from '../../assets/CommonStyles';
import { COPY_CONSTANTS } from '../../assets/CopyConstants';
import { Facility } from '../../data/models/Facility';
import { ApplicationStore } from '../../data/stores/ApplicationStore';
import { ItemKeyAndDisplayValue } from '../../util/common';
import { isEmailValid, isPhoneValid } from '../../util/validate';
import { FormTextField } from './FormTextField';
import { FormDropdownField } from './FormDropdownField';
import { Modal } from './Modal';

interface Props {
    store?: ApplicationStore;
    isVisible: boolean;
    facilityToEdit?: Facility;
    onComplete: () => void;
}

interface State {
    name: string;
    address: string;
    phone: string;
    email: string;
    orgID: string;

    contactValidationError: string;
    isValid: boolean;
    forceDisplayValidationErrors: boolean;
    orgIDsAndNames?: ItemKeyAndDisplayValue[];
    facilityIDsAndNames?: ItemKeyAndDisplayValue[];
}

@inject('store')
@observer
export class FacilityAddEditModal extends React.Component<Props, State> {

    private readonly CLASS_NAME = 'FacilityAddEditModal';

    public state = this.getInitialState();

    public static getDerivedStateFromProps(newProps: Props): Partial<State> | null {
        if (null == newProps.facilityToEdit) {
            return null;
        }

        return {
            name: newProps.facilityToEdit.name,
            address: newProps.facilityToEdit.address,
            phone: newProps.facilityToEdit.contactPhone || '',
            email: newProps.facilityToEdit.contactEmailAddress || '',
            orgID: newProps.facilityToEdit.orgID || '',
        };
    }

    private getInitialState(): State {
        return (
            this.props.facilityToEdit
                ? {
                    name: this.props.facilityToEdit.name,
                    address: this.props.facilityToEdit.address,
                    phone: this.props.facilityToEdit.contactPhone || '',
                    email: this.props.facilityToEdit.contactEmailAddress || '',
                    orgID: this.props.facilityToEdit.orgID || '',
                    contactValidationError: '',
                    isValid: true,
                    forceDisplayValidationErrors: false
                }
                : {
                    name: '',
                    address: '',
                    phone: '',
                    email: '',
                    orgID: '',
                    contactValidationError: '',
                    isValid: false,
                    forceDisplayValidationErrors: false
                }
        );
    }

    private async save(): Promise<void> {

        if (false === this.validate()) {
            this.setState({ forceDisplayValidationErrors: true });
            return;
        }

        const facility = Facility.create(
            this.state.name,
            this.state.address,
            this.state.phone,
            this.state.email,
            this.props.facilityToEdit,
            this.state.orgID
        );

        if (null == this.props.store) {
            throw new Error(`${this.CLASS_NAME}: no store`);
        }

        if (this.props.facilityToEdit) {
            await this.props.store.facilityStore.updateFacility(facility);
        } else {
            await this.props.store.facilityStore.addFacility(facility);
        }

        this.close(facility);
    }

    private close(savedFacility: Facility | undefined): void {

        this.props.onComplete();

        if (undefined === savedFacility || undefined === this.props.facilityToEdit) {
            setTimeout(() => this.setState(this.getInitialState()), 500);
        }
    }

    public componentDidUpdate(prevProps: Props): void {
        this.validate();

        if (this.props !== prevProps) {
            this.refreshOrgIDsAndDisplayNames();
            this.refreshFacilityIDsAndDisplayNames();
        }
    }

    private async refreshOrgIDsAndDisplayNames(): Promise<void> {
        if (null == this.props.store) {
            throw new Error(`${this.CLASS_NAME}: no store`);
        }

        const orgIDsAndDisplayValues = await this.props.store.orgStore.getOrgIDsAndDisplayValues();

        this.setState({ orgIDsAndNames: orgIDsAndDisplayValues });
    }

    private async refreshFacilityIDsAndDisplayNames(): Promise<void> {
        if (null == this.props.store) {
            throw new Error(`${this.CLASS_NAME}: no store`);
        }

        const facilityIDsAndDisplayValues = await this.props.store.facilityStore.getFacilityIDsAndDisplayValues();

        this.setState({ facilityIDsAndNames: facilityIDsAndDisplayValues });
    }


    private validate(): boolean {

        const contactValidationError = this.getContactValidationError();

        const isValid =
            this.getNameValidationError().length === 0
            && this.getRequiredFieldValidationError(this.state.address).length === 0
            && contactValidationError.length === 0
            && this.getPhoneValidationError().length === 0
            && this.getEmailValidationError().length === 0;

        if (contactValidationError !== this.state.contactValidationError || isValid !== this.state.isValid) {
            this.setState({
                contactValidationError: contactValidationError,
                isValid: isValid
            });
        }

        return isValid;
    }

    private getNameValidationError(): string {

        if (null == this.props.store) {
            throw new Error(`${this.CLASS_NAME}: no store`);
        }

        if (undefined === this.props.facilityToEdit || this.state.name !== this.props.facilityToEdit.name) {

            if (undefined === this.state.facilityIDsAndNames || this.state.facilityIDsAndNames.some((facilityIDAndDisplay: ItemKeyAndDisplayValue) => facilityIDAndDisplay.display === this.state.name)) {
                return COPY_CONSTANTS.facilityAlreadyExists;
            }
        }

        return this.getRequiredFieldValidationError(this.state.name);
    }

    private getPhoneValidationError(): string {

        return (
            ('' === this.state.phone || isPhoneValid(this.state.phone))
                ? ''
                : COPY_CONSTANTS.phoneInvalid
        );
    }

    private getEmailValidationError(): string {

        return (
            ('' === this.state.email || isEmailValid(this.state.email))
                ? ''
                : COPY_CONSTANTS.emailInvalid
        );
    }

    private getContactValidationError(): string {

        return (
            (this.state.phone.length === 0 && this.state.email.length === 0)
                ? COPY_CONSTANTS.phoneOrEmailRequired
                : ''
        );
    }

    private getRequiredFieldValidationError(value: string): string {
        if (value.length === 0) {
            return COPY_CONSTANTS.requiredField;
        }
        return '';
    }

    public render(): JSX.Element {

        const contactValidationError = this.getContactValidationError();

        return (
            <Modal
                isVisible={this.props.isVisible}
                title={this.props.facilityToEdit
                    ? COPY_CONSTANTS.editFacility
                    : COPY_CONSTANTS.addNewFacility}
                primaryButtonText={this.props.facilityToEdit
                    ? COPY_CONSTANTS.save
                    : COPY_CONSTANTS.addFacility}
                onPrimaryButtonClick={() => this.save()}
                onCloseButtonClick={() => this.close(undefined)}
            >

                <div style={rowStyle}>

                    <FormTextField
                        label={COPY_CONSTANTS.faciltyName}
                        value={this.state.name}
                        initialValue={this.props.facilityToEdit ? this.props.facilityToEdit.name : ''}
                        onValueChanged={(newValue: string) =>
                            this.setState({ name: newValue })
                        }
                        validationError={this.getNameValidationError()}
                        forceDisplayValidationError={this.state.forceDisplayValidationErrors}
                    />
                     {/* set up a dropdown for possible orgs. allow empty orgID  */}
                     <FormDropdownField
                        label={COPY_CONSTANTS.org}
                        items={this.state.orgIDsAndNames || []}
                        selectedItemKey={this.state.orgID || ''}
                        initialSelectedItemKey={this.props.facilityToEdit ? this.props.facilityToEdit.orgID : ''}
                        onValueChanged={(newValue: string) =>
                            this.setState({ orgID: newValue === '' ? null : newValue })
                        }
                        validationError={this.getRequiredFieldValidationError(this.state.orgID || '')}
                        forceDisplayValidationError={this.state.forceDisplayValidationErrors}
                    />

                </div>

                <div style={rowStyle}>

                    <FormTextField
                        label={COPY_CONSTANTS.telephone}
                        value={this.state.phone}
                        initialValue={this.props.facilityToEdit ? this.props.facilityToEdit.contactPhone : ''}
                        onValueChanged={(newValue: string) =>
                            this.setState({ phone: newValue })
                        }
                        validationError={
                            contactValidationError.length > 0
                                ? contactValidationError
                                : this.getPhoneValidationError()
                        }
                        forceDisplayValidationError={this.state.forceDisplayValidationErrors}
                    />

                    <FormTextField
                        style={rightColumnFieldStyle}
                        label={COPY_CONSTANTS.email}
                        value={this.state.email}
                        initialValue={this.props.facilityToEdit ? this.props.facilityToEdit.contactEmailAddress : ''}
                        onValueChanged={(newValue: string) =>
                            this.setState({ email: newValue })
                        }
                        validationError={
                            contactValidationError.length > 0
                                ? contactValidationError
                                : this.getEmailValidationError()
                        }
                        forceDisplayValidationError={this.state.forceDisplayValidationErrors}
                    />

                </div>

                <div style={rowStyle}>

                    <FormTextField
                        label={COPY_CONSTANTS.address}
                        isMultiLine={true}
                        value={this.state.address}
                        initialValue={this.props.facilityToEdit ? this.props.facilityToEdit.address : ''}
                        onValueChanged={(newValue: string) =>
                            this.setState({ address: newValue })
                        }
                        validationError={this.getRequiredFieldValidationError(this.state.address)}
                        forceDisplayValidationError={this.state.forceDisplayValidationErrors}
                    />

                </div>

            </Modal>
        );
    }

}
